import React from "react";
import type { MindMapNode } from "./types";
import type { ExpandedMap } from "./types";
import { MindMapNodeComponent } from "./MindMapNode";

interface MindMapTreeProps {
  node: MindMapNode;
  expanded: ExpandedMap;
  onToggle: (id: string) => void;
  depth?: number;
}

const MindMapTree: React.FC<MindMapTreeProps> = ({
  node,
  expanded,
  onToggle,
  depth = 0,
}) => {
  const isExpanded = !!expanded[node.id];
  const hasChildren = !!node.children?.length;

  return (
    <li className="flex flex-row items-center gap-0">
      <div className="flex flex-col items-start">
        <MindMapNodeComponent
          node={node}
          isExpanded={isExpanded}
          onToggle={onToggle}
          depth={depth}
        />
      </div>

      {hasChildren && (
        <ul
          className={`flex flex-col gap-0.5 transition-all duration-300 ease-in-out ${
            isExpanded
              ? "ml-6 max-w-[3000px] opacity-100"
              : "max-w-0 overflow-hidden opacity-0"
          }`}
        >
          {node.children!.map((child) => (
            <MindMapTree
              key={child.id}
              node={child}
              expanded={expanded}
              onToggle={onToggle}
              depth={depth + 1}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

interface MindMapTreeRootProps {
  root: MindMapNode;
  expanded: ExpandedMap;
  onToggle: (id: string) => void;
}

export const MindMapTreeRoot: React.FC<MindMapTreeRootProps> = ({
  root,
  expanded,
  onToggle,
}) => {
  const isExpanded = !!expanded[root.id];

  return (
    <div className="flex flex-row items-center gap-0">
      <div className="flex flex-col items-center">
        <MindMapNodeComponent
          node={root}
          isRoot
          isExpanded={isExpanded}
          onToggle={onToggle}
          depth={0}
        />
      </div>

      {root.children && (
        <ul
          className={`flex flex-col gap-0.5 transition-all duration-300 ease-in-out ${
            isExpanded
              ? "ml-6 max-w-[3000px] opacity-100"
              : "max-w-0 overflow-hidden opacity-0"
          }`}
        >
          {root.children.map((child) => (
            <MindMapTree
              key={child.id}
              node={child}
              expanded={expanded}
              onToggle={onToggle}
              depth={1}
            />
          ))}
        </ul>
      )}
    </div>
  );
};
